import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import ApiController from "../../controller/ApiController";
import {
  CategoryType,
  Coupons,
  storeDetailType,
  storeType,
} from "../../controller/StoreController";
import CartInto from "../store/cartInfo";

import {
  UseQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useRecoilState, useRecoilValue } from "recoil";
import { getRecoil, setRecoil } from "recoil-nexus";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { BackButton } from "../../components/BackButton";
import ExtensionCheckoutModal from "../../components/modal/ExtensionCheckoutModal";
import LogController from "../../controller/LogController";
import MessageController from "../../controller/MessageController";
import OrderController, {
  StoreOrderType,
} from "../../controller/OrderController";
import { useToast } from "../../controller/ToastController";
import { useBackTimer } from "../../hooks/useBackTimer";
import { authState, masterStoreState } from "../../recoil/auth";
import { cartPayInfo, cartPayInfoState, cartState } from "../../recoil/cart";
import { langState } from "../../recoil/lang";
import {
  checkoutDetailType,
  checkoutPayCompleteModalVisible,
  checkoutState,
} from "../../recoil/socket";
import { paymentRealmType } from "../../recoil/type";
import { getDecryptObject, getEncryptObject } from "../../utils/crypto";
import { getStoreName } from "../../utils/util";
import MenuInfo from "../store/menuInfo";

type storeDataType = {
  store: storeType;
  menu: CategoryType[];
  detail: storeDetailType[];
  coupons: Coupons[];
};

const CheckOutDetails = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const showToast = useToast();
  const cart = useRecoilValue(cartState);
  const cartPay = useRecoilValue<cartPayInfo>(cartPayInfoState);
  const storeList = location.state.storeList;
  const selectedIndex: number = location.state.storeIndex;
  const [payCompleteIsVisible, setPayCompleteIsVisible] =
    useRecoilState<boolean>(checkoutPayCompleteModalVisible);
  const [checkoutDetail, setCheckoutDetail] = useState<
    (checkoutDetailType & { hour_extension: number }) | undefined
  >(undefined);
  const [orderInfo, setOrderInfo] = useState<StoreOrderType>({
    address1: "",
    address2: "",
    area_number: 0,
    number: 0,
    area_name: "",
    name: "",
    store_id: 0,
    limit_order_price: 0,
    max_delivery_distance: "",
    base_delivery_price: 0,
    base_delivery_distance: "",
    surcharge_delivery_distance: 0,
    surcharge_delivery_price: 0,
    is_self_delivery: 0,
    delivery_call_type: 0,
    delivery_estimated_time: 0,
    delivery_prepare_time: 0,
    pickup_name: "",
    pickup_name_en: "",
    store_login_yn: true,
    next_open_time: "",
    next_close_time: "",
    store_availability_yn: true,
    store_availability: "",
    room_tip: 0,
    coupon_list: {
      availble_coupon_list: [],
      unavailable_coupon_list: [],
    },
  });
  const lang = useRecoilValue<number>(langState);
  const master = useRecoilValue(masterStoreState);

  const [storeId, setStoreId] = useState<number>(
    "store_id" in storeList
      ? storeList.store_id
      : storeList[selectedIndex].store_id
  );

  const [selectedStoreIndex, setSelectedStoreIndex] =
    useState<number>(selectedIndex);

  const [zoom, setZoom] = useState<boolean>(false);
  const [scrollTop, setScrollTop] = useState<boolean>(false);

  // 주류 1회 확인
  const [isCheckAdult, setIsCheckAdult] = useState<boolean>(false);

  // 터치 제어 로직
  const { resetTimer } = useBackTimer("checkoutDetails");

  // 메뉴이미지 축소확대
  const handleToggleZoom = () => {
    setZoom((prevZoom) => !prevZoom);
    console.log("zoom", zoom);
  };

  const { data: storeData }: UseQueryResult<storeDataType> = useQuery({
    queryKey: [
      "details",
      "store_id" in storeList
        ? storeList.store_id
        : storeList[selectedStoreIndex].store_id,
    ],
    queryFn: async () => {
      if (storeList) {
        const isStoreAlwaysSellingCheckoutProduct =
          "store_id" in storeList
            ? storeList.isStoreAlwaysSellingCheckoutProduct
            : storeList[selectedStoreIndex].isStoreAlwaysSellingCheckoutProduct;

        const oParams = {
          store_id:
            "store_id" in storeList
              ? storeList.store_id
              : storeList[selectedIndex].store_id,
          parent_store_id: master!.store_id,
          user_token_id: getRecoil(authState).user_token_id,
          user_id: getRecoil(authState).user_id,
          extend_types: [] as number[],
          isStoreAlwaysSellingCheckoutProduct:
            isStoreAlwaysSellingCheckoutProduct,
        };

        if (!isStoreAlwaysSellingCheckoutProduct) {
          oParams["extend_types"] = getRecoil(checkoutState).extend_types;
        }

        const res = await ApiController.store.getKnockStoreData(oParams);
        if (res && res.data && res.data.data) {
          return res.data.data;
        } else throw new Error("details error");
      }
    },
  });

  useEffect(() => {
    queryClient.removeQueries({
      queryKey: [
        "details",
        "store_id" in storeList
          ? storeList.store_id
          : storeList[selectedStoreIndex].store_id,
      ],
    });
  }, []);

  useEffect(() => {
    document.addEventListener("message", handleOrder);

    return () => {
      document.removeEventListener("message", handleOrder);
    };
  }, [orderInfo]);

  useEffect(() => {
    try {
      if (master) {
        LogController.navigate({
          path: "CheckoutDetails",
          params: "navigate",
          params2: `${master.store_name}`,
          store_id: master.store_id,
          index: master.store_id,
        });
      }
    } catch (err: any) {
      console.log(err);
      showToast("EEROR" + err);
    }

    OrderController.deleteCart();

    return () => {
      OrderController.deleteCart();
    };
  }, []);

  const getCheckoutData = async () => {
    try {
      const checkoutData = getRecoil(checkoutState);
      if (
        checkoutData !== undefined &&
        checkoutData.notice_detail_id !== undefined
      ) {
        const oRes = await ApiController.checkout.getCheckoutNoticeDetail(
          checkoutData.notice_detail_id
        );

        if (oRes && oRes.data.ok) {
          return oRes.data.data;
        }
      }

      return undefined;
    } catch (e) {
      console.log("err =>", e);
      return undefined;
    }
  };

  const sendCheckoutOrder = async (payResult: paymentRealmType) => {
    const cart = getRecoil(cartState);
    const cartPay = getRecoil(cartPayInfoState);

    let res = await OrderController.sendCheckoutOrder(
      cart,
      cartPay,
      payResult,
      orderInfo
    );

    if (res.ok) {
      setRecoil(cartState, []);
      const noticeDetail = await getCheckoutData();

      setCheckoutDetail({
        ...noticeDetail,
        ...{
          hour_extension: cart[0].hour_extension ? cart[0].hour_extension : 0,
        },
      });
      setPayCompleteIsVisible(true);
    } else {
      window.ReactNativeWebView?.postMessage(
        MessageController.getPayCancelRequestData(payResult)
      );
      showToast("ERROR", "error");
    }
  };

  const handleOrder = async (event: any) => {
    try {
      let message = getDecryptObject(event.data);

      if (message.type === "pay_result") {
        let payResult: paymentRealmType = message.data;

        window.ReactNativeWebView?.postMessage(
          MessageController.getEchoData({ payResult })
        );

        if (payResult.tran_result && "amount" in payResult) {
          await sendCheckoutOrder(payResult);
        } else {
          showToast(
            payResult.response_msg
              ? payResult.response_msg
              : "결제 오류입니다.",
            "error",
            3000
          );
        }
      }
    } catch (err: any) {
      window.ReactNativeWebView?.postMessage(
        getEncryptObject({
          type: "error",
          data: err,
        })
      );
    } finally {
      window.ReactNativeWebView?.postMessage(
        MessageController.getOrderCompleteData()
      );
    }
  };

  const clickCheckoutOrder = async () => {
    const realPrice = cartPay.total_amount_incl;

    if (master) {
      let res = await ApiController.store.getStoreOrderData(
        cart[0]?.store_id,
        master.store_id,
        getRecoil(authState).user_token_id,
        realPrice
      );

      if (res.ok) {
        setOrderInfo(res.data);
      }
    }

    try {
      const requestPayment = await ApiController.payment.request({});
      if (requestPayment.data.data.success) {
        let message = MessageController.getPayApprovalRequestData(
          realPrice,
          cart[0].store_id,
          requestPayment.data.data.payment_request_id
        );

        window.ReactNativeWebView?.postMessage(message);
      } else {
        // showToast("에러메시지");
      }
    } catch (err) {
      // showToast("에러메시지");
      console.log(err);
    }
  };

  return (
    <>
      <Style>
        <div className="wrap">
          <div
            className="container"
            onTouchStart={resetTimer}
            onClick={resetTimer}
            onScrollCapture={resetTimer}
          >
            <div className="header">
              <BackButton pathname="checkoutDetails" />
              <h2 className="storeName">
                {"store_id" in storeList
                  ? getStoreName(storeList, lang)
                  : getStoreName(storeList[0], lang)}
              </h2>

              <button onClick={handleToggleZoom}>
                {zoom ? (
                  <img
                    src={`${process.env.PUBLIC_URL}/knockknock/svg/zoomOut.svg`}
                    alt="메뉴 확대"
                    style={{ width: "60px" }}
                  />
                ) : (
                  <img
                    src={`${process.env.PUBLIC_URL}/knockknock/svg/zoomIn.svg`}
                    alt="메뉴 확대"
                    style={{ width: "60px" }}
                  />
                )}
              </button>
            </div>

            <div className="menuWrap">
              {storeData ? (
                <MenuInfo
                  storeId={storeId}
                  menuInfo={storeData && storeData.menu ? storeData.menu : []}
                  storeInfo={
                    storeData && storeData.store ? storeData.store : undefined
                  }
                  storeDetailInfo={
                    storeData && storeData.detail ? storeData.detail : []
                  }
                  zoom={zoom}
                  isCheckAdult={isCheckAdult}
                  setIsCheckAdult={setIsCheckAdult}
                  resetTimer={resetTimer}
                  scrollTop={scrollTop}
                  setScrollTop={setScrollTop}
                />
              ) : (
                <div className="loadingWrap">
                  <img
                    src={`${process.env.PUBLIC_URL}/knockknock/gif/loadingImage.gif`}
                    alt="로딩이미지"
                  />
                </div>
              )}
            </div>
            <div className="cartWrap">
              <CartInto
                storeInfo={
                  storeData && storeData.store ? storeData.store : undefined
                }
                coupons={
                  storeData && storeData.coupons ? storeData.coupons : undefined
                }
                resetTimer={resetTimer}
                onClickOrder={
                  cart.length > 0 && cart[0].product_type === 2
                    ? clickCheckoutOrder
                    : undefined
                }
              />
            </div>
          </div>
        </div>
      </Style>
      <ExtensionCheckoutModal checkoutDetail={checkoutDetail} />
    </>
  );
};
export default CheckOutDetails;

const Style = styled.div`
  .container {
    width: 100%;
    max-width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
  }

  .header {
    height: auto;
    max-height: 11vh;
    padding-top: 20px;
    margin: 0 4%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    .storeName {
      margin-left: -46px;
      font-size: 32px;
      font-weight: 300;
    }
    .backBtn {
      width: 36px;
    }
  }

  .storeItem {
    margin: 0 2px;
    max-width: 80px;
    display: flex;
    flex-direction: column;

    img {
      width: 100%;
      height: 70px;
      border-top-left-radius: 5px;
      border-top-right-radius: 5px;
      object-fit: cover;
      filter: brightness(50%);
    }
    img.active {
      filter: brightness(100%);
    }
    .storeTitle {
      width: 100%;
      height: 25px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #5e5e5e;
      padding: 0 6px;
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;

      p {
        color: #ffffff;
        font-size: 14px;
        line-height: 25px;
        font-weight: 300;
        margin: 0 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  /* 메뉴 */
  .menuWrap {
    overflow: auto;
  }

  /* 장바구니 */
  .cartWrap {
    height: 100%;
    max-height: 26vh;
    z-index: 999;
  }

  .loadingWrap {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    img {
      width: 80%;
      object-fit: cover;
    }
  }
`;
